import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import IndiaMap from '../../assets/home/growthmap.png';

const GrowthSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [animatedStats, setAnimatedStats] = useState({
        trips: 0,
        fleetOwners: 0,
        companies: 0,
        drivers: 0
    });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const sectionRef = useRef(null);
    const animationTimersRef = useRef([]);
    const hasAnimatedRef = useRef(false);

    // Final statistics
    const finalStats = useMemo(() => ({
        trips: 40000,
        fleetOwners: 2500,
        companies: 300,
        drivers: 8000
    }), []);

    // Cleanup function for animation timers
    const cleanupTimers = useCallback(() => {
        animationTimersRef.current.forEach(timer => clearInterval(timer));
        animationTimersRef.current = [];
    }, []);

    // Counter animation function with proper cleanup
    const animateCounter = useCallback((start, end, duration, callback) => {
        const startTime = Date.now();
        const timer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const current = Math.floor(start + (end - start) * progress);
            callback(current);

            if (progress === 1) {
                clearInterval(timer);
                // Remove timer from ref array
                animationTimersRef.current = animationTimersRef.current.filter(t => t !== timer);
            }
        }, 16);

        animationTimersRef.current.push(timer);
        return timer;
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimatedRef.current) {
                    setIsVisible(true);
                    hasAnimatedRef.current = true;

                    // Start counter animations with delays - exactly as in original
                    setTimeout(() => {
                        animateCounter(0, finalStats.trips, 2000, (val) =>
                            setAnimatedStats(prev => ({ ...prev, trips: val }))
                        );
                    }, 500);

                    setTimeout(() => {
                        animateCounter(0, finalStats.fleetOwners, 2000, (val) =>
                            setAnimatedStats(prev => ({ ...prev, fleetOwners: val }))
                        );
                    }, 700);

                    setTimeout(() => {
                        animateCounter(0, finalStats.companies, 2000, (val) =>
                            setAnimatedStats(prev => ({ ...prev, companies: val }))
                        );
                    }, 900);

                    setTimeout(() => {
                        animateCounter(0, finalStats.drivers, 2000, (val) =>
                            setAnimatedStats(prev => ({ ...prev, drivers: val }))
                        );
                    }, 1100);
                }
            },
            {
                threshold: 0.3,
            }
        );

        const currentRef = sectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
            cleanupTimers();
        };
    }, [animateCounter, finalStats, cleanupTimers]);

    // Handle image loading
    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
        setImageError(true);
        console.error('Failed to load India map image');
    }, []);

    return (
        <section
            ref={sectionRef}
            className="w-full relative overflow-hidden"
            aria-labelledby="growth-section-title"
            role="region"
        >
            {/* Heading Section - Exactly as original */}
            <header className="w-full bg-white pt-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <h2
                        id="growth-section-title"
                        className={`text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        Our <span className="text-[#F05A28]">Growth</span> Story
                    </h2>
                    <p
                        className={`mt-4 text-lg text-gray-600 max-w-3xl mx-auto transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.2s' }}
                    >
                        From a vision to reality - see how RoadExpress has transformed logistics across India with innovative solutions and reliable partnerships.
                    </p>
                </div>
            </header>

            {/* Main Content Section - Preserving your exact design */}
            <div className="w-full bg-white relative">
                <div className="max-w-7xl mx-auto px-5 relative">
                    <div className="grid lg:grid-cols-2 items-center relative">

                        {/* Your original black background div - kept exactly as is */}
                        <div
                            className="absolute left-1/2 transform -translate-x-1/2 bg-black z-10"
                            style={{
                                width: '131%',
                                height: '78%',
                                left: 0
                            }}
                            aria-hidden="true"
                        ></div>

                        {/* Left Side - Content - Exactly as original with accessibility additions */}
                        <div className="space-y-12 relative z-20 py-16">
                            {/* Title */}
                            <div
                                className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[-50px] opacity-0'
                                    }`}
                                style={{ transitionDelay: '0.2s' }}
                            >
                                <h3 className="text-3xl md:text-4xl lg:text-4xl font-bold leading-tight text-white">
                                    Expanding Nationwide with<br />
                                    <span className="text-[#F05A28]">Clear, Efficient, and Profitable</span><br />
                                    Logistics
                                </h3>
                            </div>

                            {/* Statistics Grid - Exactly as original */}
                            <div className="grid grid-cols-2 gap-8">
                                {/* Stat 1 */}
                                <div
                                    className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                        }`}
                                    style={{ transitionDelay: '0.4s' }}
                                >
                                    <div className="text-4xl md:text-4xl font-bold text-[#F05A28] mb-2">
                                        <span aria-live="polite">
                                            {animatedStats.trips.toLocaleString()}+
                                        </span>
                                    </div>
                                    <div className="text-lg text-gray-300">Successful Trips</div>
                                </div>

                                {/* Stat 2 */}
                                <div
                                    className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                        }`}
                                    style={{ transitionDelay: '0.6s' }}
                                >
                                    <div className="text-4xl md:text-4xl font-bold text-[#F05A28] mb-2">
                                        <span aria-live="polite">
                                            {animatedStats.fleetOwners.toLocaleString()}+
                                        </span>
                                    </div>
                                    <div className="text-lg text-gray-300">Fleet Owners</div>
                                </div>

                                {/* Stat 3 */}
                                <div
                                    className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                        }`}
                                    style={{ transitionDelay: '0.8s' }}
                                >
                                    <div className="text-4xl md:text-4xl font-bold text-[#F05A28] mb-2">
                                        <span aria-live="polite">
                                            {animatedStats.companies}+
                                        </span>
                                    </div>
                                    <div className="text-lg text-gray-300">Corporate<br />Companies</div>
                                </div>

                                {/* Stat 4 */}
                                <div
                                    className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                        }`}
                                    style={{ transitionDelay: '1s' }}
                                >
                                    <div className="text-4xl md:text-4xl font-bold text-[#F05A28] mb-2">
                                        <span aria-live="polite">
                                            {animatedStats.drivers}+
                                        </span>
                                    </div>
                                    <div className="text-lg text-gray-300">Onboarded Trucks</div>
                                </div>
                            </div>

                            {/* CTA Button - Exactly as original */}
                            <div
                                className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                    }`}
                                style={{ transitionDelay: '1.2s' }}
                            >
                                <button
                                    className="group bg-[#F05A28] hover:bg-[#e04a18] text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center space-x-2 focus:outline-none focus:ring-4 focus:ring-[#F05A28]/50"
                                    aria-label="Join our team - Explore career opportunities"
                                >
                                    <span>Join our team</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                </button>
                            </div>
                        </div>

                        {/* Right Side - India Map - Exactly as original with image loading */}
                        <div
                            className={`relative transform transition-all duration-1000 z-20 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[50px] opacity-0'
                                }`}
                            style={{ transitionDelay: '0.6s' }}
                        >
                            <div className="relative py-16">
                                {!imageError ? (
                                    <img
                                        src={IndiaMap}
                                        alt="India Map showing RoadExpress network coverage across the country"
                                        className={`w-full max-h-md mx-auto filter drop-shadow-2xl transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                        loading="lazy"
                                    />
                                ) : (
                                    // Simple fallback that matches original style
                                    <div className="w-full max-h-md mx-auto flex items-center justify-center bg-gray-200 rounded-lg aspect-square filter drop-shadow-2xl">
                                        <div className="text-gray-500 text-center">
                                            <div className="text-4xl mb-2">🇮🇳</div>
                                            <div>India Map</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* SEO Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "RoadExpress",
                    "description": "Leading logistics company with nationwide coverage in India",
                    "areaServed": "India",
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "4.8",
                        "ratingCount": finalStats.trips
                    }
                })}
            </script>
        </section>
    );
};

export default GrowthSection;